import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingBlock } from './Spinner';
import i18n from '../i18n';
import { api } from '../api/client';
import type { Account } from '../api/client';
import { Select, Label, ListBox } from '@heroui/react';
import type { Key } from 'react-aria-components';
import styles from './CopyConfigModal.module.css';

type CopyType = 'mass' | 'priority' | 'automated' | 'modelContext' | 'conversationalStyle';

interface CopyConfigModalProps {
  type: CopyType;
  currentAccountId: string;
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

interface ListItem {
  id: number;
  name: string;
  description?: string | null;
}

interface ModelContextField {
  id: string;
  label: string;
  description: string;
}

const MODEL_CONTEXT_FIELDS: ModelContextField[] = [
  { id: 'base_information', label: i18n.t('account:copyConfig.fields.baseInformation'), description: i18n.t('account:copyConfig.fields.baseInformationDesc') },
  { id: 'physical_traits', label: i18n.t('account:copyConfig.fields.physicalTraits'), description: i18n.t('account:copyConfig.fields.physicalTraitsDesc') },
  { id: 'preferences_and_hobbies', label: i18n.t('account:copyConfig.fields.preferencesAndHobbies'), description: i18n.t('account:copyConfig.fields.preferencesAndHobbiesDesc') },
  { id: 'sexual_fetishes_and_positioning_in_sex', label: i18n.t('account:copyConfig.fields.sexualPreferences'), description: i18n.t('account:copyConfig.fields.sexualPreferencesDesc') },
  { id: 'content_inventory', label: i18n.t('account:copyConfig.fields.contentOnTheAccount'), description: i18n.t('account:copyConfig.fields.contentOnTheAccountDesc') },
  { id: 'family_information', label: i18n.t('account:copyConfig.fields.familyInformation'), description: i18n.t('account:copyConfig.fields.familyInformationDesc') },
  { id: 'additional_details', label: i18n.t('account:copyConfig.fields.additionalDetails'), description: i18n.t('account:copyConfig.fields.additionalDetailsDesc') },
  { id: 'emotional_patterns_and_triggers', label: i18n.t('account:copyConfig.fields.emotionalPatterns'), description: i18n.t('account:copyConfig.fields.emotionalPatternsDesc') },
  { id: 'memories', label: i18n.t('account:copyConfig.fields.memories'), description: i18n.t('account:copyConfig.fields.memoriesDesc') },
  { id: 'current_life_context', label: i18n.t('account:copyConfig.fields.currentLifeContext'), description: i18n.t('account:copyConfig.fields.currentLifeContextDesc') },
  { id: 'conversational_dynamics', label: i18n.t('account:copyConfig.fields.conversationalDynamics'), description: i18n.t('account:copyConfig.fields.conversationalDynamicsDesc') },
];

const CONVERSATIONAL_STYLE_FIELDS: ModelContextField[] = [
  { id: 'conversational_dynamics', label: i18n.t('account:copyConfig.fields.normalChatStyle'), description: i18n.t('account:copyConfig.fields.normalChatStyleDesc') },
  { id: 'hot_conversational_dynamics', label: i18n.t('account:copyConfig.fields.hotChatStyle'), description: i18n.t('account:copyConfig.fields.hotChatStyleDesc') },
  { id: 'normal_chat_emojis', label: i18n.t('account:copyConfig.fields.normalChatEmojis'), description: i18n.t('account:copyConfig.fields.normalChatEmojisDesc') },
  { id: 'hot_chat_emojis', label: i18n.t('account:copyConfig.fields.hotChatEmojis'), description: i18n.t('account:copyConfig.fields.hotChatEmojisDesc') },
];

/** Copy types that copy account text fields (vs. message lists). */
const isFieldCopyType = (t: CopyType) => t === 'modelContext' || t === 'conversationalStyle';

export function CopyConfigModal({ type, currentAccountId, isOpen, onClose, onComplete }: CopyConfigModalProps) {
  const { t } = useTranslation('account');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [sourceAccountId, setSourceAccountId] = useState<string>('');
  const [items, setItems] = useState<ListItem[] | ModelContextField[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Get modal title based on type
  const getTitle = () => {
    switch (type) {
      case 'mass':
        return t('copyConfig.copyMassMessageLists');
      case 'priority':
        return t('copyConfig.copyPriorityMessageLists');
      case 'automated':
        return t('copyConfig.copyAutomatedMessageLists');
      case 'modelContext':
        return t('copyConfig.copyModelContext');
      case 'conversationalStyle':
        return t('copyConfig.copyConversationalStyle');
      default:
        return t('copyConfig.copyConfiguration');
    }
  };

  // Load accounts on open
  useEffect(() => {
    if (isOpen) {
      loadAccounts();
      setSourceAccountId('');
      setItems([]);
      setSelectedItems(new Set());
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  // Load items when source account changes
  useEffect(() => {
    if (sourceAccountId && !isFieldCopyType(type)) {
      loadItems();
    } else if (sourceAccountId && type === 'modelContext') {
      setItems(MODEL_CONTEXT_FIELDS);
    } else if (sourceAccountId && type === 'conversationalStyle') {
      setItems(CONVERSATIONAL_STYLE_FIELDS);
    }
  }, [sourceAccountId, type]);

  const loadAccounts = async () => {
    try {
      const allAccounts = await api.getAccounts();
      // Filter out current account
      const otherAccounts = allAccounts.filter(acc => acc.account_id !== currentAccountId);
      setAccounts(otherAccounts);
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setError(t('copyConfig.loadAccountsFailed'));
    }
  };

  const loadItems = async () => {
    if (!sourceAccountId) return;

    setLoadingItems(true);
    setError(null);

    try {
      let loadedItems: ListItem[] = [];

      if (type === 'mass') {
        const lists = await api.getMassMessagesLists(sourceAccountId);
        loadedItems = lists.map(list => ({
          id: list.id,
          name: list.name,
          description: list.description,
        }));
      } else if (type === 'priority') {
        const lists = await api.getPriorityMassMessagesLists(sourceAccountId);
        loadedItems = lists.map(list => ({
          id: list.id,
          name: list.name,
          description: list.description,
        }));
      } else if (type === 'automated') {
        const lists = await api.getAutomatedMessagesLists(sourceAccountId);
        loadedItems = lists.map(list => ({
          id: list.list_id,
          name: list.list_name,
          description: list.description,
        }));
      }

      setItems(loadedItems);
    } catch (err) {
      console.error('Failed to load items:', err);
      setError(t('copyConfig.loadItemsFailed'));
    } finally {
      setLoadingItems(false);
    }
  };

  const handleToggleItem = (id: string | number) => {
    const newSelection = new Set(selectedItems);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedItems(newSelection);
  };

  const handleSelectAll = () => {
    if (isFieldCopyType(type)) {
      const allIds = (items as ModelContextField[]).map(field => field.id);
      setSelectedItems(new Set(allIds));
    } else {
      const allIds = (items as ListItem[]).map(item => item.id);
      setSelectedItems(new Set(allIds));
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
  };

  const handleCopy = async () => {
    if (!sourceAccountId || selectedItems.size === 0) {
      setError(t('copyConfig.selectSourceAndItem'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (isFieldCopyType(type)) {
        const fields = Array.from(selectedItems) as string[];
        await api.copyModelContext(currentAccountId, sourceAccountId, fields);
      } else {
        const listIds = Array.from(selectedItems) as number[];
        
        if (type === 'mass') {
          await api.copyMassMessagesLists(currentAccountId, sourceAccountId, listIds);
        } else if (type === 'priority') {
          await api.copyPriorityMessagesLists(currentAccountId, sourceAccountId, listIds);
        } else if (type === 'automated') {
          await api.copyAutomatedMessagesLists(currentAccountId, sourceAccountId, listIds);
        }
      }

      setSuccess(true);
      setTimeout(() => {
        onComplete();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to copy:', err);
      setError(err.message || t('copyConfig.copyConfigFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{getTitle()}</h2>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.modalBody}>
          {success ? (
            <div className={styles.successMessage}>
              {t('copyConfig.successCopied')}
            </div>
          ) : (
            <>
              <div className={styles.section}>
                <Select
                  placeholder={t('copyConfig.selectAnAccount')}
                  selectedKey={sourceAccountId || null}
                  onSelectionChange={(key: Key | null) => {
                    setSourceAccountId(key ? String(key) : '');
                    setSelectedItems(new Set());
                  }}
                  isDisabled={loading}
                  className="w-full"
                >
                  <Label className="block font-medium mb-2 text-[var(--text-secondary)] text-sm">{t('copyConfig.sourceAccount')}</Label>
                  <Select.Trigger className="w-full px-4 py-3 rounded-xl bg-[var(--bg-input)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] hover:border-[var(--accent-primary)] transition-all duration-300">
                    <Select.Value />
                    <Select.Indicator />
                  </Select.Trigger>
                  <Select.Popover className="bg-[var(--bg-card)] border border-[var(--border-color)] rounded-lg shadow-xl z-[1100]">
                    <ListBox className="p-1 max-h-60 overflow-auto">
                      {accounts.map(acc => (
                        <ListBox.Item
                          key={acc.account_id}
                          id={acc.account_id}
                          textValue={acc.profile_name || acc.username || acc.account_id}
                          className="px-3 py-2 rounded-md text-sm text-[var(--text-primary)] cursor-pointer hover:bg-[var(--bg-hover)] data-[selected]:bg-[var(--accent-primary)]/10 data-[selected]:text-[var(--accent-primary)]"
                        >
                          {acc.profile_name || acc.username || acc.account_id}
                        </ListBox.Item>
                      ))}
                    </ListBox>
                  </Select.Popover>
                </Select>
              </div>

              {sourceAccountId && (
                <div className={styles.section}>
                  <div className={styles.sectionHeader}>
                    <label>
                      {isFieldCopyType(type) ? t('copyConfig.selectFieldsToCopy') : t('copyConfig.selectItemsToCopy')}
                    </label>
                    <div className={styles.selectButtons}>
                      <button onClick={handleSelectAll} className={styles.textButton}>
                        {t('copyConfig.selectAll')}
                      </button>
                      <button onClick={handleDeselectAll} className={styles.textButton}>
                        {t('copyConfig.deselectAll')}
                      </button>
                    </div>
                  </div>

                  {loadingItems ? (
                    <LoadingBlock label={t('copyConfig.loadingItems')} />
                  ) : items.length === 0 ? (
                    <div className={styles.emptyState}>
                      {t('copyConfig.noItemsAvailable')}
                    </div>
                  ) : (
                    <div className={styles.itemsList}>
                      {items.map((item) => {
                        const itemId = 'id' in item ? item.id : (item as ModelContextField).id;
                        const itemName = 'name' in item ? item.name : (item as ModelContextField).label;
                        const itemDesc = 'description' in item && item.description 
                          ? item.description 
                          : (item as ModelContextField).description;

                        return (
                          <div key={itemId} className={styles.checkboxItem}>
                            <label>
                              <input
                                type="checkbox"
                                checked={selectedItems.has(itemId)}
                                onChange={() => handleToggleItem(itemId)}
                                disabled={loading}
                              />
                              <span className={styles.itemName}>{itemName}</span>
                              {itemDesc && (
                                <span className={styles.itemDescription}>{itemDesc}</span>
                              )}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className={styles.errorMessage}>{error}</div>
              )}
            </>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button
            onClick={onClose}
            className={styles.cancelButton}
            disabled={loading}
          >
            {t('copyConfig.cancel')}
          </button>
          <button
            onClick={handleCopy}
            className={styles.copyButton}
            disabled={loading || !sourceAccountId || selectedItems.size === 0 || success}
          >
            {loading
              ? t('copyConfig.copying')
              : isFieldCopyType(type)
                ? t('copyConfig.copyFields', { count: selectedItems.size })
                : t('copyConfig.copyItems', { count: selectedItems.size })}
          </button>
        </div>
      </div>
    </div>
  );
}
